<template>
  <modal
    :name="modalTitle"
    :adaptive="true"
    height="auto"
    :width="500"
    transition="linear"
    @opened="handleOpenOnModal"
    @before-close="handleAddChildModalOnClosing"
  >
    <form
      name="addChildForm"
      class="fp-modal"
      novalidate
      @submit.prevent
      autocomplete="off"
      id="addChildModal"
    >

      <modals-header
        :modalNameProp="modalTitle"
        @hideModalProp="hideAddChildModal"
      >
      </modals-header>

      <hr class="v--modal-hr">

      <input
        autocomplete="off"
        name="hidden"
        type="text"
        class="hidden"
      >

      <!-- FIRST NAME -->
      <div class="part">
        <p class="label input-filter-label">
          {{ $lang.messages.firstName }}
          <span class="asterix">*</span>
        </p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('addChild.firstName')}">
          <input
            type="text"
            name="firstName"
            class="input"
            :placeholder="$lang.messages.enterChildFirstName"
            v-model="childToAdd.firstName"
            data-vv-scope="addChild"
            v-validate="'required|min:1|max:32'"
            @click.self="clearInputErrors($event)"
            @focus.self="clearInputErrors($event)"
            @blur="clearOthers($event)"
          />
          <img
            src="@/assets/images/validation_ico.png"
            alt=""
            class="icon error-icon"
          />
          <div
            v-show="errors.has('addChild.firstName')"
            class="error-tooltip"
            :class="{'hide-tooltip' : bluredProp !== 'firstName'}"
          >
            <div class="text">
              {{ errors.first('addChild.firstName') }}
            </div>
          </div>
        </div>
      </div>

      <!-- LAST NAME -->
      <div class="part">
        <p class="label input-filter-label">
          {{ $lang.messages.lastName }}
          <span class="asterix">*</span>
        </p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('addChild.lastName')}">
          <input
            type="text"
            name="lastName"
            class="input"
            :placeholder="$lang.messages.enterChildLastName"
            v-model="childToAdd.lastName"
            data-vv-scope="addChild"
            v-validate="'required|min:1|max:32'"
            @click.self="clearInputErrors($event)"
            @focus.self="clearInputErrors($event)"
            @blur="clearOthers($event)"
          />
          <img
            src="@/assets/images/validation_ico.png"
            alt=""
            class="icon error-icon"
          />
          <div
            v-show="errors.has('addChild.lastName')"
            class="error-tooltip"
            :class="{'hide-tooltip' : bluredProp !== 'lastName'}"
          >
            <div class="text">
              {{ errors.first('addChild.lastName') }}
            </div>
          </div>
        </div>
      </div>

      <!-- DATE OF BIRTH -->
      <div class="part short-part datepicker-part">
        <p class="label input-filter-label">
          {{ $lang.messages.dateOfBirth }}
          <span class="asterix">*</span>
        </p>
        <f-p-datepicker
          :dateOfBirth="true"
          :option="childToAdd.birthDate.toString()"
          @optionChanged="onSelectedBirthDate" >
        </f-p-datepicker>
      </div>

      <!-- ADD COPARENT -->
      <div
        v-if="childActionType === 'add'"
        class="part"
      >
        <p class="label input-filter-label">
          {{ $lang.messages.coparent }}
          <span class="asterix">*</span>
        </p>
        <div class="wrapper">
          <autocomplete
            v-if="coparents.length !== 0"
            :items="coparents"
            :placeholder="$lang.messages.selectCoparent"
            :name="'coparent'"
            :scope="'addChild'"
            :bluredProp="bluredProp"
            :option="preselectedCoparent"
            @optionChanged="onSelectedCoparent"
            @clearInputErrors="clearInputErrors"
            @clearOthers="clearOthers"
            :key="rerender"
            ref="autocompleteCoparent"
          />
        </div>
      </div>

      <div class="part">
        <p class="label input-filter-label">
          <span class="asterix">*</span> {{ $lang.messages.mandatoryFields }}
        </p>
      </div>

      <!-- CLOSE MODAL-->
      <button
        type="button"
        class="generic-btn"
        @click="hideAddChildModal"
      >
        {{ $lang.messages.back }}
      </button>

      <!-- SUBMIT -->
      <f-p-button-loader :loading="loading" :text="editOrSaveChild" @click.native="onAddChildFormSubmitted" ></f-p-button-loader>

    </form>
  </modal>
</template>

<script>
  import {mapActions, mapGetters} from "vuex";
  import {
    GET_RELATED_COPARENTS,
    ADD_CHILD,
    EDIT_CHILD,
    GET_CASES,
    GET_REGISTRATION_PROGRESS
  } from "@/store/action.types";
  import { dateFormat, requestDateFormatUTC, requestDateFormat } from '@/mixins/dateFormat';
  import Autocomplete from "@/components/common/Autocomplete";
  import modalsHeader from "@/components/common/modals/ModalsHeader";
  import FPDatepicker from '@/components/common/FPDatepicker';
  import FPButtonLoader from "@/components/common/FPButtonLoader";
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "addChild",
    inject: ['$validator'],
    components: {
      Autocomplete,
      'modals-header': modalsHeader,
      FPDatepicker,
      'f-p-button-loader': FPButtonLoader
    },
    mixins: [
      dateFormat,
      requestDateFormatUTC,
      requestDateFormat
    ],
    data() {
      return {
        coparents: [],
        rerender: 0,
        childActionType: '',
        childToAdd: {
          firstName: '',
          lastName: '',
          birthDate: new Date()
        },
        modalTitle: this.$lang.messages.addChildModal,
        editOrSaveChild: this.$lang.messages.save,
        bluredProp: '',
        typeable: true,
        preselectedCoparent: {},
        loading: false,
      };
    },
    computed: {
      ...mapGetters(['coparentGuid', 'isAppLocked'])
    },
    methods: {
      ...mapActions({
        getRelatedCoparentsAction: GET_RELATED_COPARENTS,
        addChildAction: ADD_CHILD,
        editChildAction: EDIT_CHILD,
        getCasesAction: GET_CASES,
        getRegistrationProgressAction: GET_REGISTRATION_PROGRESS
      }),
      handleOpenOnModal() {
        let formElement = document.getElementById('addChildModal')
        formElement.addEventListener('click', this.handleClick)
      },
      /**
       * @description When user closes the modal send the emmited value to UserProfile
       * so we could turn on/off the registration tooltip
       */
       handleAddChildModalOnClosing() {
        this.$emit('handleAddChildModalOnClosing', false);
      },
      show(childActionTypeParam, childToAddParam) {
        this.childActionType = childActionTypeParam;
        this.childToAdd = childToAddParam;
        this.childToAdd.birthDate = this.requestDateFormat(childToAddParam.birthDate);

        if (childActionTypeParam === 'add') {
          this.clearAddChildData();
          this.modalTitle = this.$lang.messages.addChildModal;
          this.editOrSaveChild = this.$lang.messages.addChildModal;
        } else if (childActionTypeParam === 'edit') {
          this.modalTitle = this.$lang.messages.editChildModal;
          this.editOrSaveChild = this.$lang.messages.save;
        }
        this.loadCoparents();
        window.setTimeout(() => {
            this.$modal.show(this.modalTitle)
        }, 0);
        this.errors.clear();
      },
      hideAddChildModal() {
        this.checkAnyInput()
        this.$modal.hide(this.modalTitle)
      },
      clearAddChildData() {
        this.childToAdd.firstName = ''
        this.childToAdd.lastName = ''
        this.childToAdd.birthDate = new Date()
        this.childToAdd.birthDate.setDate(this.childToAdd.birthDate.getDate() - 1)
        this.childToAdd.coparent = {}
        this.loadCoparents()
        this.rerender = this.rerender + 1
        this.$validator.reset()
        this.checkAnyInput()
      },
      loadCoparents() {
        this.getRelatedCoparentsAction()
        .then(response => {
          this.coparents = response.data.data.filter(item => item.guid !== this.coparentGuid);
          if(this.coparents.length === 1) {
            this.preselectedCoparent = this.coparents[0]
          }
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      onSelectedCoparent(value) {
        let coparentValid = false;
        this.coparents.forEach(element => {
          if (element.name === value.name) {
            coparentValid = true
          }
        });

        if (coparentValid === true) {
          this.childToAdd.coparent = value
        } else {
          this.childToAdd.coparent = {}
        }
      },
      onSelectedBirthDate(value) {
        this.childToAdd.birthDate = value;
      },
      onAddChildFormSubmitted() {
        if (!this.loading) {
          this.$validator.validateAll('addChild').then(result => {
            this.checkAnyInput();
            let coparentValid = true;
            if (this.childActionType === 'add' && (this.childToAdd.coparent.guid === undefined || this.childToAdd.coparent.guid === '')) {
              coparentValid = false;
              this.$refs.autocompleteCoparent.createError()
            } else {
              this.childToAdd.birthDate = this.requestDateFormatUTC(this.childToAdd.birthDate);
            }

            if (result && coparentValid) {
              this.loading = true;
              if (this.childActionType === 'add') {
                this.addChildAction({
                  firstName: this.childToAdd.firstName,
                  lastName: this.childToAdd.lastName,
                  coparentGuid: this.childToAdd.coparent.guid,
                  birthDate: this.childToAdd.birthDate
                })
                .then(() => {
                  // Call /account/setup/progress for registration wizard
                  this.getRegistrationProgressAction()
                })
                .then(() => {
                  if (!this.isAppLocked) {
                    return this.getCasesAction()
                    .then(() => {
                      this.$parent.loadUserProfile(false, true);
                      this.$snotify.success(this.$lang.messages.sn_childAdded, "SUCCESS");
                    });
                  } else {
                    this.$parent.loadUserProfile(false, true);
                    this.$snotify.success(this.$lang.messages.sn_childAdded, "SUCCESS");
                  }
                })
                .catch(error => {
                  this.hideAddChildModal();
                  if (error.response) {
                    if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                      this.$snotify.error(error.response.data.status.desc, "ERROR");
                    }
                  }
                })
                .finally(() => {
                  this.loading = false;
                  this.hideAddChildModal();
                })
              } else if (this.childActionType === 'edit') {
                this.editChildAction({
                  firstName: this.childToAdd.firstName,
                  lastName: this.childToAdd.lastName,
                  birthDate: this.childToAdd.birthDate,
                  guid: this.childToAdd.guid
                })
                .then(() => {
                  this.$snotify.success(this.$lang.messages.sn_childEdited, "SUCCESS");
                  if (!this.isAppLocked) {
                    this.getCasesAction()
                    .then(() => {
                      this.loading = false;
                      this.hideAddChildModal();
                      this.$parent.loadUserProfile(false, false);
                    })
                    .catch(error => {
                      this.loading = false;
                      if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                        this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
                      }
                    });
                  } else {
                    this.loading = false;
                    this.hideAddChildModal();
                    this.$parent.loadUserProfile(false, false);
                  }
              })
              .catch(error => {
                this.hideAddChildModal();
                if (error.response) {
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, "ERROR");
                  }
                }
              });
              }
            }
          });
        }
      },
      clearInputErrors(e) {
        if (e.target) {
          this.$validator.errors.remove('addChildModal.' + e.target.name)
        } else {
          this.$validator.errors.remove('addChildModal.' + e)
        }
      },
      checkAnyInput() {
        this.bluredProp = ''
      },
      clearOthers(e) {
        this.bluredProp = e.target.name
      },
      handleClick(e) {
        if (this.childActionType === 'add' && this.$refs.autocompleteCoparent) {
          this.$refs.autocompleteCoparent.handleClickOutside(e)
          // this.$refs.autocompleteCoparent.onOutsideClickModal('addChildModal', this.$refs.autocompleteCoparent.name)
        }
      },
      /* DISABLED - need to find solution for vee-validate inside modals */
      // Update the input value we are sending to FPInputTextValidate
      // inputTextValueFirstName (val) {
      //   this.childToAdd.firstName = val
      // },
      // inputTextValueLastName (val) {
      //   this.childToAdd.lastName = val
      // },
    }
  };
</script>
